import { Controller } from "@hotwired/stimulus"
import * as maplibregl from "maplibre-gl"
import maplibreWorkerUrl from "maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url"

// maplibre-gl v6 is ESM-only (no default export) and no longer locates its own
// worker under a bundler: `import.meta.url` inside Vite's module graph does not
// point at the dist worker, so the map would mount and never request a tile.
// `?worker&url` — not plain `?url` — routes the file through Vite's worker
// pipeline and emits a self-contained chunk; the dist worker imports its
// sibling `maplibre-gl-shared.mjs`, which `?url` would leave out of a
// production build. Same-origin URL, so no `worker-src blob:` CSP exemption.
maplibregl.setWorkerUrl(maplibreWorkerUrl)

// Stop status → marker hex color
const STATUS_COLOR = {
  pending: "#6366f1", // indigo-500
  checked_in: "#22c55e", // green-500
  skipped: "#9ca3af", // gray-400
}

export default class extends Controller {
  static values = {
    styleUrl: String,
    apiKey: String,
    route: Object,
    attribution: String,
  }

  static targets = ["canvas"]

  connect() {
    if (!this.apiKeyValue) {
      return
    }

    const apiKey = this.apiKeyValue
    const styleUrl = this.styleUrlValue

    // Open centred on the route, not [0,0] (Gulf of Guinea). fitBounds on
    // `load` refines the framing, but seeding the initial centre from the first
    // valid stop means the map never flashes the wrong continent even if the
    // container isn't laid out when fitBounds runs (Turbo Native WebView).
    const initialStops = this._validStops(this.routeValue?.stops)
    const initialCenter =
      initialStops.length > 0
        ? [initialStops[0].longitude, initialStops[0].latitude]
        : [0, 0]

    this._map = new maplibregl.Map({
      container: this.hasCanvasTarget ? this.canvasTarget : this.element,
      style: styleUrl,
      center: initialCenter,
      zoom: initialStops.length > 0 ? 6 : 2,
      attributionControl: false,
      transformRequest: (url) => {
        if (url.includes("amazonaws.com")) {
          const separator = url.includes("?") ? "&" : "?"
          return { url: `${url}${separator}key=${apiKey}` }
        }
        return { url }
      },
    })

    // Compact attribution: AWS/HERE require attribution to be present (§82 AWS
    // Service Terms) so we can't drop it, but `compact: true` collapses it to a
    // small "ⓘ" in the corner that expands on tap — no more banner overlapping
    // the stop list below the map.
    const attribution = this.attributionValue || "© HERE 2026"
    this._map.addControl(
      new maplibregl.AttributionControl({
        compact: true,
        customAttribution: attribution,
      }),
      "bottom-right",
    )

    this._map.on("load", () => this._renderRoute())
  }

  disconnect() {
    if (this._map) {
      this._map.remove()
      this._map = null
    }
  }

  // Stops with finite, real lng/lat. The serializer emits null coords for a
  // stop with no resolved location; a null poisons maplibre bounds/sources
  // ("Expected value to be of type number, but found null") and would drag the
  // viewport to [0,0]. Coerce + drop the bad ones defensively.
  _validStops(stops) {
    return (stops || [])
      .map((s) => ({
        ...s,
        longitude: this._toCoord(s.longitude),
        latitude: this._toCoord(s.latitude),
      }))
      .filter(
        (s) => Number.isFinite(s.longitude) && Number.isFinite(s.latitude),
      )
  }

  // Coerce a coordinate to a number, but treat null / undefined / "" as invalid
  // (NaN) rather than letting Number() silently turn them into 0 — otherwise a
  // stop with a null/blank coord would survive as [0, 0] (Gulf of Guinea) and
  // drag the viewport there, the very bug _validStops exists to prevent.
  _toCoord(value) {
    if (value === null || value === undefined || value === "") return NaN
    return Number(value)
  }

  _renderRoute() {
    const route = this.routeValue
    if (!route || !route.stops || route.stops.length === 0) return

    const stops = this._validStops(route.stops)
    if (stops.length === 0) return

    // Build GeoJSON feature collection for stops
    const features = stops.map((stop) => ({
      type: "Feature",
      geometry: {
        type: "Point",
        coordinates: [stop.longitude, stop.latitude],
      },
      properties: {
        id: stop.id,
        sequence: stop.sequence,
        label: stop.label,
        status: stop.status,
        color: STATUS_COLOR[stop.status] ?? STATUS_COLOR.pending,
      },
    }))

    this._map.addSource("route-stops", {
      type: "geojson",
      data: {
        type: "FeatureCollection",
        features,
      },
    })

    // Circle layer for stop markers
    this._map.addLayer({
      id: "route-stops-circle",
      type: "circle",
      source: "route-stops",
      paint: {
        "circle-radius": 14,
        "circle-color": ["get", "color"],
        "circle-stroke-width": 2,
        "circle-stroke-color": "#ffffff",
      },
    })

    // Symbol layer for sequence numbers
    this._map.addLayer({
      id: "route-stops-label",
      type: "symbol",
      source: "route-stops",
      layout: {
        "text-field": ["to-string", ["get", "sequence"]],
        "text-size": 12,
        "text-font": ["Noto Sans Bold"],
        "text-anchor": "center",
      },
      paint: {
        "text-color": "#ffffff",
      },
    })

    // Polyline layer if route has encoded geometry
    if (route.polyline) {
      this._map.addSource("route-line", {
        type: "geojson",
        data: {
          type: "Feature",
          geometry: {
            type: "LineString",
            coordinates: this._decodePolyline(route.polyline),
          },
        },
      })
      this._map.addLayer({
        id: "route-line-layer",
        type: "line",
        source: "route-line",
        layout: { "line-join": "round", "line-cap": "round" },
        paint: {
          "line-color": "#6366f1",
          "line-width": 3,
          "line-opacity": 0.75,
        },
      })
    }

    // Fit bounds to all stops. resize() first so the container's real size is
    // known — the Turbo Native WebView can finish laying out after the map
    // inits, which otherwise leaves fitBounds computing against a 0-height box.
    if (stops.length > 0) {
      const bounds = new maplibregl.LngLatBounds()
      stops.forEach((stop) => bounds.extend([stop.longitude, stop.latitude]))
      this._map.resize()
      this._map.fitBounds(bounds, { padding: 48, maxZoom: 13 })
    }
  }

  // HERE Flexible Polyline decode (6-digit precision, 2D)
  _decodePolyline(encoded) {
    if (!encoded) return []
    try {
      // Minimal decode: returns [[lng, lat], ...] pairs
      // HERE uses a custom encoding but AWS Location returns standard Google polyline
      // in some responses. Both use the same sign-encoding trick.
      const coords = []
      let index = 0
      let lat = 0
      let lng = 0
      while (index < encoded.length) {
        let b,
          shift = 0,
          result = 0
        do {
          b = encoded.charCodeAt(index++) - 63
          result |= (b & 0x1f) << shift
          shift += 5
        } while (b >= 0x20)
        lat += result & 1 ? ~(result >> 1) : result >> 1

        shift = 0
        result = 0
        do {
          b = encoded.charCodeAt(index++) - 63
          result |= (b & 0x1f) << shift
          shift += 5
        } while (b >= 0x20)
        lng += result & 1 ? ~(result >> 1) : result >> 1

        coords.push([lng / 1e5, lat / 1e5])
      }
      return coords
    } catch (_e) {
      return []
    }
  }
}
